import React from "react";
import { FieldGroupRow, FieldGroupRowStatus } from "../../../models/intake";
import FormConstants from "./DataEntryFieldsConstants";
import {
    writeFieldToForm, ClassicFieldMeta, classicFieldErrorMessage, toDisplayDate, isUtcMidnightDate,
} from "./formFieldHelpers";
import DateField from "../../../common/formFields/DateField";
import { DEFAULT_MIN_DATE } from "../../../common/constants/ApiConstants";

export interface RequestDateFieldProps {
    /** Currently selected Request date (read from DATA_ENTRY_FIELDS_FORM). */
    readonly value?: Date | string;
    /** Hides the field entirely - classic pane's requestType != DISCLOSURE_ACCOUNTING gate. */
    readonly visible: boolean;
    readonly dispatch: Function;
    readonly formName?: string;
    /**
     * Extra, non-form side effect the caller wants to run on change (the
     * classic pane's autosave PATCH). The base DATA_ENTRY_FIELDS_FORM write
     * is always handled by this component itself via writeFieldToForm -
     * callers should NOT also dispatch a redundant form write here.
     */
    readonly onRequestDateChange?: (newValue?: Date | string) => void;
    /** AB#38460: unusable text to keep on screen, and the channel that reports it. Forwarded verbatim to DateField - see its own docblocks. */
    readonly invalidText?: string;
    readonly onInvalidText?: (text?: string, committed?: boolean) => void;
    /** AB#38460: forwarded verbatim to DateField - see its docblock. */
    readonly resyncKey?: string | number;
    /**
     * Real redux-form validation state, threaded down from
     * DataEntryDetailsRightPane.tsx (Spec #34102 / F1d regression fix -
     * restores "Request date is required." text on a blocked Next-click).
     * Omitted by Quick Entry's own call site.
     */
    readonly meta?: ClassicFieldMeta;
}

/**
 * Shared Request Date field (Spec #34098 / F1a). Always required when
 * visible (unlike Authorization Date, which is conditionally required).
 * Rendered by both the classic RightPane Details tab and the new Quick
 * Entry row.
 */
const RequestDateField: React.FC<RequestDateFieldProps> = (props) => {
    if (!props.visible) {
        return null;
    }

    const formName = props.formName ?? FormConstants.DATA_ENTRY_FIELDS_FORM;

    const onChange = (value?: Date | string) => {
        writeFieldToForm(props.dispatch, FormConstants.REQUEST_DATE, value, formName);
        props.onRequestDateChange?.(value);
    };

    return (
        <DateField
            label="Request date"
            placeholder="mm/dd/yyyy"
            maxDate={new Date()}
            minDate={DEFAULT_MIN_DATE}
            required
            errMsg={classicFieldErrorMessage(props.meta)}
            invalidText={props.invalidText}
            onInvalidText={props.onInvalidText}
            resyncKey={props.resyncKey}
            onChange={onChange}
            // AB#37843: display-only normalization - see toDisplayDate. The write
            // path (onChange above) is untouched; only what the picker SHOWS changes.
            value={toDisplayDate(props.value)}
            // Restores mirrorData(FormConstants.REQUEST_DATE, date) firing on
            // change/blur - keeps transactionDetails.date.request in sync, same
            // as before the F1a extraction (Spec #34102 / F1d regression fix).
            name={FormConstants.REQUEST_DATE}
        />
    );
};

export default RequestDateField;

export const REQUEST_DATE_ROW_ID = "requestDate";

/**
 * MM/DD/YYYY for a Date or date string (yyyy-MM-dd / ISO); date-only strings parsed by parts to
 * avoid a timezone day-shift. AB#37982: a Date branches on isUtcMidnightDate exactly like
 * formatShortFieldGroupDate (formFieldHelpers.ts) - a UTC-midnight Date (AI-extracted) reads its
 * own UTC calendar day; any other Date (specialist-picked/typed, local midnight) reads its own
 * LOCAL calendar day. Unconditional toISOString() shifted a picked/typed date to the PREVIOUS day
 * in any timezone AHEAD of UTC.
 */
function formatReqDateValue(v: Date | string): string {
    let isoDate: string | undefined;
    if (typeof v === "string") {
        isoDate = v;
    } else if (v instanceof Date && !isNaN(v.getTime())) {
        isoDate = isUtcMidnightDate(v)
            ? v.toISOString().substring(0, 10)
            : `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
    }
    const m = isoDate?.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[2]}/${m[3]}/${m[1]}` : String(v);
}

/** Value-based equality for Date | string request dates. */
function sameReqDate(a: Date | string | undefined, b: Date | string | undefined): boolean {
    if (!a || !b) {
        return false;
    }
    const ta = a instanceof Date ? a.getTime() : new Date(a).getTime();
    const tb = b instanceof Date ? b.getTime() : new Date(b).getTime();
    return !isNaN(ta) && ta === tb;
}

/**
 * Builds the FieldGroupRow for the Request Date row - always required when
 * visible. `aiValue` is the AI-extracted request date; DataEntryDetails seeds it
 * into the form field, so the row shows it (MM/DD/YYYY) and reads as
 * AI-pre-selected (green) until the specialist edits it.
 */
export function mapRequestDateToFieldGroupRow(badgeNumber: number, formValue: Date | string | undefined, aiValue?: Date | string): FieldGroupRow {
    const hasValue = formValue !== undefined && formValue !== null && formValue !== "";

    return {
        id: REQUEST_DATE_ROW_ID,
        badgeNumber,
        status: hasValue ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.NeedsEntry,
        label: "Request Date",
        // F1c (#34101) seeds the extracted request date and marks the row
        // AI-pre-selected when the current value still matches the extracted one.
        // formatReqDateValue handles both Date and the string form the API
        // returns (the same raw-toString bug F1d's formatFieldGroupDate fixed).
        value: hasValue ? formatReqDateValue(formValue!) : null,
        isAiPreSelected: Boolean(aiValue) && sameReqDate(formValue, aiValue),
    };
}
